import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'src/containers/withRouter/withRouter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Tabs,
  DataTable,
  FunnelChart,
  Tooltip,
  parseQuery,
  Row,
  Col,
  VirtualList,
  prepareFiltersQuery,
} from '@jjj/components';
import { isEqual } from 'lodash';
import startCase from 'lodash/startCase';
import Wrapper, { TabContentWrapper } from './poolList.style';
import { OrganizationsTooltipWrapper } from '../../../../../landingPage/poolsBreakdown/poolsBreakdownList.style';
import actions from './actions';
import { getColumns } from './columns';
import { tableConfig } from '../../../../../../settings';
import { t } from '../../../../../../i18n';
import {
  poolsViewBulkActionGAEventClick,
  poolsViewModalFunnelTabViewedGAEvent,
  poolsViewRowItemActionGAEventClick,
  selectSortingColumnGAEvent,
} from '../../../../../../helpers/googleAnalytics/events';
import { translateFilterOptions } from '../../../../../../helpers/filters';
import ActionSentAlert from './components/actionSentAlert';
import { getLogo } from '../../../../../../helpers/image';
import AgencyInvites from './tabs/agencies/agencyInvites';

export const TABS = {
  all: 'all',
  myTeam: 'myTeam',
  funnel: 'funnel',
  agencies: 'agencies',
};

export const filtersConfig = {
  prefix: 'pools_pref_',
};

export const STATUS_KEY = 'status';

class PoolList extends Component {
  state = {
    tab: TABS.all,
    isBulkTooltipVisible: false,
    shouldResetScroll: false,
    selectedSortingColumn: '',
    selectedSortingOrder: '',
  };

  componentDidMount() {
    const {
      booking: { id },
    } = this.props;

    this.props.loadData({ id, pageSize: tableConfig.virtualTablePageSize });
  }

  componentDidUpdate(prevProps) {
    if (this.props.bulkActionsEnabled) {
      if (!isEqual(prevProps.data.filters, this.props.data.filters)) {
        this.props.clearSelection();
        this.setState({ shouldResetScroll: true });
      }

      if (prevProps.data.bulkByField !== this.props.data.bulkByField) {
        this.loadAvailableBulkActions();
      }
    }
  }

  componentWillUnmount() {
    this.props.clearStore();
  }

  onResetPosition = () => {
    this.setState({ shouldResetScroll: false });
  };

  onTabClick = selectedTab => {
    if (selectedTab === TABS.funnel) {
      poolsViewModalFunnelTabViewedGAEvent();
    }

    if (this.state.tab !== selectedTab) {
      this.props.history.push({ search: '' });
    }

    this.setState({ tab: selectedTab }, () => this.loadData());
  };

  popoverContent = pool => {
    return pool.actions.map(action => {
      const actionKey = action?.key || action?.name.toLowerCase().replace(' ', '_');
      const actionFallbackName = action?.name || startCase(action.key.replace(/_/g, ' '));

      return {
        ...action,
        name: t(`dash.jobs.bookings.poolList.actions.${actionKey}`, {}, actionFallbackName),
        onActionClick: () => this.processAction(pool, action),
      };
    });
  };

  processAction = (pool, action) => {
    this.props.sendRequest({
      url: action.url,
      method: action.method,
      bookingId: this.props.booking.id,
      poolId: pool.id,
      key: action.key,
      tab: this.state.tab,
    });

    const { tab, selectedSortingColumn, selectedSortingOrder } = this.state;

    if (tab === TABS.all) {
      poolsViewRowItemActionGAEventClick(action.key);

      if (selectedSortingColumn && selectedSortingOrder) {
        poolsViewRowItemActionGAEventClick(action.key, selectedSortingColumn, selectedSortingOrder);
      }
    }
  };

  getBulkByFieldQuery = () => {
    const { bulkByField } = this.props.data;

    if (!bulkByField) {
      return null;
    }

    const { field, value } = bulkByField;

    switch (field) {
      case STATUS_KEY:
        return {
          [STATUS_KEY]: [value.id],
          everyone: true,
        };
      default:
        return null;
    }
  };

  getBulkParams = () => {
    const { selectedTalents, excludedTalents, isBulkSelected, filters } = this.props.data;

    const { search = '' } = this.props.history.location || {};
    const query = parseQuery(search);

    return {
      everyone: isBulkSelected,
      booking_pool_ids_to_include: selectedTalents,
      booking_pool_ids_to_exclude: excludedTalents,
      filter: query?.filter,
      ...prepareFiltersQuery(filters),
      ...this.getBulkByFieldQuery(),
    };
  };

  processBulkAction = action => {
    this.props.sendRequest({
      url: action.url,
      method: action.method,
      data: this.getBulkParams(),
      key: action.options.key,
      tab: this.state.tab,
    });

    const { tab, selectedSortingColumn, selectedSortingOrder } = this.state;

    if (tab === TABS.all) {
      poolsViewBulkActionGAEventClick(action.key);

      if (selectedSortingColumn && selectedSortingOrder) {
        poolsViewBulkActionGAEventClick(action.key, selectedSortingColumn, selectedSortingOrder);
      }
    }
  };

  loadAvailableBulkActions = () => {
    this.props.loadAvailableAction({ bookingId: this.props.booking.id, data: this.getBulkParams() });
  };

  loadData = params => {
    const { tab } = this.state;
    const { id } = this.props.booking;

    if (tab === TABS.all) {
      this.props.loadData({
        ...params,
        id,
        pageSize: tableConfig.virtualTablePageSize,
      });

      this.setState({ selectedSortingColumn: params?.column || '', selectedSortingOrder: params?.order || '' });

      if (params?.column && params?.order) {
        selectSortingColumnGAEvent('pools_view', params.column, params.order);
      }
    } else {
      this.setState({ selectedSortingColumn: '', selectedSortingOrder: '' });
    }

    if (tab === TABS.myTeam) {
      this.props.loadMyTeamData({ ...params, id });
    }
  };

  formatLegend = values => {
    const [name] = values;
    const [, notes] = values;

    return (
      <Fragment>
        <span>{name}</span>
        <Tooltip placement="bottom" title={notes && notes.length && notes.map(item => <div key={item}>{item}</div>)}>
          {notes && notes.length > 0 && (
            <span className="info-icon">
              <FontAwesomeIcon icon="info-circle" />
            </span>
          )}
        </Tooltip>
      </Fragment>
    );
  };

  formatSegment = values => {
    const [workers_count] = values;
    const [, organizations] = values;

    return (
      <Tooltip
        placement="bottom"
        popupAlign={{ offset: [0, -8] }}
        title={
          !!organizations?.length && (
            <OrganizationsTooltipWrapper>
              {organizations.map(item => {
                const { organization, pool_percentage } = item;
                const { id } = organization;

                return (
                  <div className="organization-item" key={id}>
                    {getLogo(organization, 'logo')}
                    <span className="text">{pool_percentage}%</span>
                  </div>
                );
              })}
            </OrganizationsTooltipWrapper>
          )
        }
      >
        <span className="segment-tooltip-value">{workers_count}</span>
      </Tooltip>
    );
  };

  setSelected = (item, value) => {
    this.props.changeChecked({ item, value });
  };

  handleBulkTooltipVisibleChange = visible => {
    this.setState({ isBulkTooltipVisible: visible });

    if (visible) {
      this.loadAvailableBulkActions();
    }
  };

  onSelectByStatusClick = status => {
    this.props.selectBulkByField(status);
  };

  isSearchByStatusAvailable = () => {
    return !this.props.data.filters.some(filter => filter.name === STATUS_KEY && filter.activeFilters.length);
  };

  onTalentClick = params => {
    if (!params.current_organization) {
      return false;
    }

    return window.open(`/talent/${params.worker_id}`, '_blank');
  };

  render() {
    const { tab, shouldResetScroll } = this.state;
    const {
      pool,
      loading,
      filteredTotal,
      filters,
      page,
      pageSize,
      selectedTalents,
      excludedTalents,
      isBulkSelected,
      bulkByField,
      availableActions,
      availableStatuses,
      isAvailableActionsLoading,
      isBulkInviteSentAlertVisible,
    } = this.props.data;

    const {
      booking: { ma_booking_filtering_data },
      bulkActionsEnabled,
      isAdvancedPoolWorkersGrid,
    } = this.props;
    const { search = '' } = this.props.history.location || {};
    const query = parseQuery(search);

    const funnelConfig = {
      large: true,
      data: ma_booking_filtering_data,
      title: t('dash.jobs.bookings.pools_funnel_title'),
      value: {
        fields: ['workers_count', 'organizations'],
        formatter: values => {
          return values && this.formatSegment(values);
        },
      },
      legend: {
        fields: ['name', 'note'],
        formatter: values => {
          return values && this.formatLegend(values);
        },
      },
    };

    const excludedTalentsExtended = pool
      ? [...excludedTalents, ...pool.filter(item => !item.current_organization).map(item => item.id)]
      : excludedTalents;

    return (
      <Wrapper>
        <Tabs
          animated={false}
          onTabClick={this.onTabClick}
          activeKey={tab}
          defaultActiveKey={this.state.tab}
          destroyOnHidden
          items={[
            {
              label: t('dash.jobs.bookings.pools_all_tab_title'),
              key: TABS.all,
              children: (
                <TabContentWrapper>
                  <VirtualList
                    columns={getColumns(
                      this,
                      false,
                      {
                        selectedTalents,
                        excludedTalents,
                        isBulkSelected,
                        bulkByField,
                        bulkActionsEnabled,
                        availableActions,
                        availableStatuses,
                        isAvailableActionsLoading,
                      },
                      this.state.isBulkTooltipVisible,
                      filteredTotal,
                    )}
                    dataSource={pool}
                    loadData={this.loadData}
                    loading={loading}
                    onChange={this.loadData}
                    filter={query.filter}
                    filters={translateFilterOptions(filters, 'dash.jobs.bookings.pools_filter_options')}
                    total={filteredTotal}
                    filtersConfig={filtersConfig}
                    params={{
                      page,
                      pageSize: tableConfig.virtualTablePageSize,
                    }}
                    selectionData={{
                      selectedTalents,
                      excludedTalents: excludedTalentsExtended,
                      isBulkSelected,
                      bulkByField,
                    }}
                    onFilterChange={this.props.clearSelection}
                    additionalCustomElement={
                      isBulkInviteSentAlertVisible && (
                        <ActionSentAlert toggleBulkInviteAlertVisibility={this.props.toggleBulkInviteAlertVisibility} />
                      )
                    }
                    shouldResetScroll={shouldResetScroll}
                    onResetPosition={this.onResetPosition}
                    leftStickyColumnKey="select_all"
                    rightStickyColumnKey="actions"
                    useFiltersColumn={isAdvancedPoolWorkersGrid}
                  />
                </TabContentWrapper>
              ),
            },
            {
              label: t('dash.jobs.bookings.pools_my_team_tab_title'),
              key: TABS.myTeam,
              children: (
                <TabContentWrapper>
                  <DataTable
                    columns={getColumns(this, true)}
                    dataSource={pool}
                    loadData={this.loadData}
                    loading={loading}
                    onChange={this.loadData}
                    filter={query.filter}
                    filters={translateFilterOptions(filters, 'dash.jobs.bookings.pools_filter_options')}
                    total={filteredTotal}
                    filtersConfig={filtersConfig}
                    params={{ page, pageSize }}
                  />
                </TabContentWrapper>
              ),
            },
            {
              label: t('dash.jobs.bookings.pools_funnel_tab_title'),
              key: TABS.funnel,
              children: (
                <TabContentWrapper>
                  <Row>
                    <Col lg={14} md={20} xs={24}>
                      {ma_booking_filtering_data && ma_booking_filtering_data.length ? (
                        <FunnelChart config={funnelConfig} />
                      ) : (
                        t('dash.common.tabs.no_data')
                      )}
                    </Col>
                  </Row>
                </TabContentWrapper>
              ),
            },
            {
              label: t('dash.jobs.bookings.agencies_invites_tab_title'),
              key: TABS.agencies,
              children: (
                <AgencyInvites bookingId={this.props.booking.id} isTabOpen={this.state.tab === TABS.agencies} />
              ),
            },
          ]}
        />
      </Wrapper>
    );
  }
}

export default connect(
  state => ({
    data: state.Pool,
    view: state.App.view,
    isAdvancedPoolWorkersGrid: state.StartAppSettings?.feature_flags?.advanced_pool_workers_grid,
    bulkActionsEnabled: state.Job?.job?.bulk_actions_enabled,
  }),
  dispatch => ({
    loadData: /* istanbul ignore next */ data => dispatch(actions.loadData(data)),
    loadMyTeamData: /* istanbul ignore next */ data => dispatch(actions.loadMyTeamData(data)),
    loadAgenciesData: /* istanbul ignore next */ data => dispatch(actions.loadAgenciesData(data)),
    clearStore: /* istanbul ignore next */ () => dispatch(actions.clearStore()),
    sendRequest: /* istanbul ignore next */ data => dispatch(actions.sendRequest(data)),
    changeChecked: /* istanbul ignore next */ data => dispatch(actions.changeChecked(data)),
    clearSelection: /* istanbul ignore next */ () => dispatch(actions.clearSelection()),
    toggleBulkInviteAlertVisibility: /* istanbul ignore next */ () =>
      dispatch(actions.toggleBulkInviteAlertVisibility()),
    loadAvailableAction: /* istanbul ignore next */ data => dispatch(actions.loadAvailableAction(data)),
    selectBulkByField: /* istanbul ignore next */ data => dispatch(actions.selectBulkByField(data)),
  }),
)(withRouter(PoolList));
